// ==========================
// FILE: src/components/PasswordVault.js
// ==========================
// Spacing-only tweaks for the Add / Edit modals. Core logic untouched.

import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import { useAccess } from "../access/AccessContext";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import "../styles/layout.css";

/* ---------- Icons ---------- */
import {
  SiFacebook,
  SiInstagram,
  SiTiktok,
  SiX, // X (formerly Twitter)
  SiLinkedin,
  SiYoutube,
  SiSnapchat,
  SiPinterest,
  SiReddit,
  SiWhatsapp,
  SiDiscord,
  SiGithub,
} from "react-icons/si";
import {
  LuKey, // fallback
  LuMonitor, // Apple/Microsoft
  LuGamepad2, // PlayStation/Xbox/Steam
  LuCreditCard, // PayPal
  LuShoppingBag, // Amazon
  LuMail, // Gmail
  LuGlobe, // Google (generic)
} from "react-icons/lu";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import VideoLightbox from "./ui/VideoLightbox"; // ✅ use shared lightbox

/* ---------- Crypto helpers (UNCHANGED) ---------- */
async function getOrCreateSalt(user, vaultRef) {
  const snap = await getDoc(vaultRef);
  if (snap.exists() && snap.data().salt) return snap.data().salt;
  const rand = window.crypto.getRandomValues(new Uint8Array(16));
  const salt = btoa(String.fromCharCode(...rand));
  if (snap.exists()) {
    await updateDoc(vaultRef, { salt });
  } else {
    await setDoc(vaultRef, { salt }, { merge: true });
  }
  return salt;
}

const PBKDF2_ITER = 100_000;
async function getKey(pw, salt_b64) {
  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    enc.encode(pw),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  const salt = Uint8Array.from(atob(salt_b64), (c) => c.charCodeAt(0));
  return window.crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: PBKDF2_ITER, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encrypt(text, pw, salt) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await getKey(pw, salt);
  const ct = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(text)
  );
  return btoa(
    String.fromCharCode(...iv) + String.fromCharCode(...new Uint8Array(ct))
  );
}

export async function decrypt(b64, pw, salt) {
  const bin = atob(b64);
  const iv = Uint8Array.from(bin.slice(0, 12), (c) => c.charCodeAt(0));
  const ct = Uint8Array.from(bin.slice(12), (c) => c.charCodeAt(0));
  const key = await getKey(pw, salt);
  const dec = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ct
  );
  return new TextDecoder().decode(dec);
}

/* ---------- Helper for DigitalPlatforms page (UNCHANGED) ---------- */
export async function getVaultLabels(user) {
  if (!user) return [];
  const vaultRef = doc(db, "passwordVault", user.uid);
  const snap = await getDoc(vaultRef);
  if (!snap.exists()) return [];
  return (snap.data().entries || []).map((e) => e.label);
}

/* ---------- Networks list ---------- */
const NETWORKS = [
  // Stable social logos:
  { value: "facebook", label: "Facebook", Icon: SiFacebook },
  { value: "instagram", label: "Instagram", Icon: SiInstagram },
  { value: "tiktok", label: "TikTok", Icon: SiTiktok },
  { value: "x", label: "X (Twitter)", Icon: SiX },
  { value: "linkedin", label: "LinkedIn", Icon: SiLinkedin },
  { value: "youtube", label: "YouTube", Icon: SiYoutube },
  { value: "snapchat", label: "Snapchat", Icon: SiSnapchat },
  { value: "pinterest", label: "Pinterest", Icon: SiPinterest },
  { value: "reddit", label: "Reddit", Icon: SiReddit },
  { value: "whatsapp", label: "WhatsApp", Icon: SiWhatsapp },
  { value: "discord", label: "Discord", Icon: SiDiscord },
  { value: "github", label: "GitHub", Icon: SiGithub },

  // Extras (robust Lucide generics):
  { value: "apple", label: "Apple", Icon: LuMonitor },
  { value: "microsoft", label: "Microsoft", Icon: LuMonitor },
  { value: "google", label: "Google", Icon: LuGlobe },
  { value: "gmail", label: "Gmail", Icon: LuMail },
  { value: "playstation", label: "PlayStation", Icon: LuGamepad2 },
  { value: "xbox", label: "Xbox", Icon: LuGamepad2 },
  { value: "steam", label: "Steam", Icon: LuGamepad2 },
  { value: "paypal", label: "PayPal", Icon: LuCreditCard },
  { value: "amazon", label: "Amazon", Icon: LuShoppingBag },

  { value: "other", label: "Other", Icon: null },
];

/* Map label text to an icon for cards */
function iconForLabel(label) {
  if (!label) return LuKey;
  const L = label.toLowerCase();
  const match = NETWORKS.find(
    (n) => L.includes(n.value) || L.includes(n.label.toLowerCase())
  );
  return match?.Icon || LuKey;
}

export default function PasswordVault({ user }) {
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [state, setState] = useState("loading"); // loading, setup, unlock, unlocked
  const [masterPw, setMasterPw] = useState("");
  const [masterPw2, setMasterPw2] = useState("");
  const [inputPw, setInputPw] = useState("");
  const [vault, setVault] = useState([]);
  const [testVal, setTestVal] = useState("");
  const [salt, setSalt] = useState("");
  const [error, setError] = useState("");

  // Add modal state
  const [showAdd, setShowAdd] = useState(false);
  const [network, setNetwork] = useState("");
  const [otherNetwork, setOtherNetwork] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newValue2, setNewValue2] = useState("");

  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [editNetwork, setEditNetwork] = useState("");
  const [editOtherNetwork, setEditOtherNetwork] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [editValue, setEditValue] = useState("");
  const [editValue2, setEditValue2] = useState("");
  const [editShow, setEditShow] = useState(false);
  const [editReadOnly, setEditReadOnly] = useState(false);

  const [copiedIdx, setCopiedIdx] = useState(null);
  const pwInputRef = useRef();

  // DP UI state (for unlocked view)
  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const { readOnly } = useAccess() || { readOnly: false };

  // Load vault
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const vaultRef = doc(db, "passwordVault", user.uid);
        console.log("[PasswordVault] loading", {
          uid: user.uid,
          email: user.email || "",
          vaultPath: `passwordVault/${user.uid}`
        });
        const snap = await getDoc(vaultRef);
        console.log("[PasswordVault] initial snap", {
          exists: snap.exists(),
          data: snap.exists() ? snap.data() : null
        });
        const userSalt = await getOrCreateSalt(user, vaultRef);
        console.log("[PasswordVault] salt resolved", { hasSalt: !!userSalt });
        setSalt(userSalt);
        if (!snap.exists()) {
          setState("setup");
        } else {
          setVault(snap.data().entries || []);
          setTestVal(snap.data().test || "");
          setState("unlock");
        }
      } catch (e) {
        console.error("[PasswordVault] load failed", e);
        setError("Vault load failed: " + (e?.message || e));
      }
    })();
  }, [user]);

  /* ---------- Handlers (core logic preserved) ---------- */
  async function handleSetMasterPw(e) {
    e.preventDefault();
    if (readOnly) {
      setError("Managers can unlock only — owner must set the master password.");
      return;
    }
    setError("");
    if (masterPw.length < 8) return setError("Password must be at least 8 characters.");
    if (masterPw !== masterPw2) return setError("Passwords do not match.");
    try {
      const vaultRef = doc(db, "passwordVault", user.uid);
      const userSalt = await getOrCreateSalt(user, vaultRef);
      const testEncrypted = await encrypt("vault_test", masterPw, userSalt);
      await setDoc(vaultRef, { test: testEncrypted, entries: [], salt: userSalt });
      setTestVal(testEncrypted);
      setVault([]);
      setState("unlocked");
    } catch (e2) {
      console.error("[PasswordVault] handleSetMasterPw failed", e2);
      setError("Error setting master password: " + e2.message);
    }
  }

  async function handleUnlock(e) {
    e.preventDefault();
    setError("");
    try {
      const vaultRef = doc(db, "passwordVault", user.uid);
      const userSalt = await getOrCreateSalt(user, vaultRef);
      const result = await decrypt(testVal, inputPw, userSalt);
      if (result !== "vault_test") throw new Error("Wrong password");
      setMasterPw(inputPw);
      setState("unlocked");
      setInputPw("");
    } catch (e2) {
      console.error("[PasswordVault] handleUnlock failed", e2);
      setError("Incorrect master password.");
      setInputPw("");
      pwInputRef.current?.focus();
    }
  }

  async function handleAdd() {
    if (readOnly) return;
    setError("");
    if (!newLabel || !newValue) return setError("Fill in all fields.");
    try {
      const vaultRef = doc(db, "passwordVault", user.uid);
      const encrypted = await encrypt(newValue, masterPw, salt);
      const newEntry = { label: newLabel, value: encrypted, time: Date.now() };
      await updateDoc(vaultRef, { entries: arrayUnion(newEntry) });
      setVault((v) => [...v, newEntry]);
      setShowAdd(false);
      setNetwork("");
      setOtherNetwork("");
      setNewLabel("");
      setNewValue("");
      setNewValue2("");
    } catch (e2) {
      console.error("[PasswordVault] handleAdd failed", e2);
      setError("Error saving: " + e2.message);
    }
  }

  function handleAddGuard(e) {
    e.preventDefault();
    if (readOnly) return;
    if (free && vault.length >= 1) {
      setShowUpgrade(true);
      return;
    }
    setError("");
    if (!network) return setError("Please select a network.");
    if (network === "other" && !otherNetwork.trim())
      return setError("Please specify the network.");
    if (!newValue || !newValue2) return setError("Enter and confirm the password.");
    if (newValue !== newValue2) return setError("Passwords do not match.");

    if (network === "other") {
      setNewLabel(otherNetwork.trim());
    } else {
      const n = NETWORKS.find((n) => n.value === network);
      setNewLabel(n?.label || "");
    }
    Promise.resolve().then(() => handleAdd());
  }

  async function handleDelete(i) {
    if (readOnly) return;
    if (!window.confirm("Delete this password?")) return;
    const entry = vault[i];
    try {
      const vaultRef = doc(db, "passwordVault", user.uid);
      await updateDoc(vaultRef, { entries: arrayRemove(entry) });
      setVault((v) => v.filter((_, idx) => idx !== i));
    } catch (e2) {
      console.error("[PasswordVault] handleDelete failed", e2);
      setError("Error deleting: " + e2.message);
    }
  }

  async function openEdit(i) {
    setError("");
    setEditIdx(i);
    setEditReadOnly(readOnly);
    const entry = vault[i];

    const match = NETWORKS.find(
      (n) =>
        entry.label.toLowerCase().includes(n.value) ||
        entry.label.toLowerCase().includes(n.label.toLowerCase())
    );
    if (match && match.value !== "other") {
      setEditNetwork(match.value);
      setEditOtherNetwork("");
      setEditLabel(match.label);
    } else {
      setEditNetwork("other");
      setEditOtherNetwork(entry.label);
      setEditLabel(entry.label);
    }

    try {
      const val = await decrypt(entry.value, masterPw, salt);
      setEditValue(val);
      setEditValue2(val);
      setEditShow(false);
      setEditOpen(true);
    } catch {
      setEditValue("");
      setEditValue2("");
      setEditOpen(true);
      setError("Could not decrypt this entry.");
    }
  }

  function closeEdit() {
    setEditOpen(false);
    setEditIdx(null);
    setEditNetwork("");
    setEditOtherNetwork("");
    setEditLabel("");
    setEditValue("");
    setEditValue2("");
    setEditShow(false);
    setEditReadOnly(false);
  }

  async function handleEditSave() {
    if (readOnly) return;
    setError("");
    if (!editLabel) return setError("Please choose a network.");
    if (!editValue || !editValue2)
      return setError("Enter and confirm the password.");
    if (editValue !== editValue2) return setError("Passwords do not match.");

    const i = editIdx;
    const oldEntry = vault[i];
    try {
      const encrypted = await encrypt(editValue, masterPw, salt);
      const newEntry = { ...oldEntry, label: editLabel, value: encrypted, time: Date.now() };
      const vaultRef = doc(db, "passwordVault", user.uid);
      await updateDoc(vaultRef, { entries: arrayRemove(oldEntry) });
      await updateDoc(vaultRef, { entries: arrayUnion(newEntry) });
      setVault((v) => {
        const next = [...v];
        next[i] = newEntry;
        return next;
      });
      closeEdit();
    } catch (e2) {
      console.error("[PasswordVault] handleEditSave failed", e2);
      setError("Error saving: " + e2.message);
    }
  }

  /* ---------- Render ---------- */
  if (state === "loading") {
    return (
      <div className="container">
        <div className="empty-state">Loading vault...</div>
      </div>
    );
  }

  if (state === "setup") {
    return (
      <div className="container">
        <form className="modal-card pv-standalone" onSubmit={handleSetMasterPw}>
          <h3>Set Master Password</h3>

          {/* inner padding wrapper so fields don't hit edges; responsive */}
          <div className="pv-form-inner">
            <label className="label" htmlFor="mpw">Master Password</label>
            <input
              id="mpw"
              type="password"
              className="input"
              placeholder="Enter a strong password"
              value={masterPw}
              onChange={(e) => setMasterPw(e.target.value)}
              required
            />

            <label className="label" htmlFor="mpw2">Confirm Password</label>
            <input
              id="mpw2"
              type="password"
              className="input"
              placeholder="Retype to confirm"
              value={masterPw2}
              onChange={(e) => setMasterPw2(e.target.value)}
              required
            />
          </div>

          {error && <div className="empty-state" role="alert">{error}</div>}

          <div className="form-actions">
            <button className="btn btn-main" type="submit">Set Password</button>
            <button className="btn btn-cancel" type="button" onClick={() => setState("setup")}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (state === "unlock") {
    return (
      <div className="container">
        <form className="modal-card pv-standalone" onSubmit={handleUnlock} style={{ textAlign: "center" }}>
          <h3>Enter Master Password</h3>

          {/* inner padding wrapper */}
          <div className="pv-form-inner">
            <label className="label" htmlFor="unlockpw" style={{ textAlign: "left" }}>Master Password</label>
            <input
              id="unlockpw"
              type="password"
              className="input"
              placeholder="Enter your master password"
              value={inputPw}
              onChange={(e) => setInputPw(e.target.value)}
              ref={pwInputRef}
              required
            />
          </div>

          {error && <div className="empty-state" role="alert">{error}</div>}

          <div className="form-actions">
            <button className="btn btn-main" type="submit">Unlock</button>
            {!readOnly && (
              <button
                className="btn btn-danger"
                type="button"
                onClick={async () => {
                  if (!window.confirm("This will delete all stored passwords.")) return;
                  const vaultRef = doc(db, "passwordVault", user.uid);
                  await setDoc(vaultRef, { test: "", entries: [], salt }, { merge: true });
                  setState("setup");
                }}
              >
                Reset Vault
              </button>
            )}
            
          </div>
        </form>
      </div>
    );
  }

  // UNLOCKED
  const ActionsDesktop = () => (
    <div className="dp-help-row">
      <button type="button" className="btn dp-help read" onClick={() => setShowDrawer(true)}>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button type="button" className="btn dp-help see" onClick={() => setShowVideo(true)}>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );
  const ActionsMobile = () => (
    <div className="dp-help-row">
      <button type="button" className="btn dp-help read" onClick={() => setShowDrawer(true)}>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button type="button" className="btn dp-help see" onClick={() => setShowVideo(true)}>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );

  return (
    <div className="container">
      <div className="header-row">
        <h2 className="section-title" style={{ margin: 0 }}>Password Vault</h2>
        <div className="header-actions desktop-only"><ActionsDesktop /></div>
        {!readOnly && (
          <button className="btn-add" onClick={() => setShowAdd(true)} title="Add new password">
            +
          </button>
        )}
      </div>

      {/* Hero + mobile actions */}
      <div className="hero-wrap">
        <img src="/lastwish-account-password-vault.jpg" alt="Password Vault" className="hero-img" />
      </div>
      <div className="mobile-only" style={{ margin: "10px 0" }}>
        <ActionsMobile />
      </div>

      <div className="grid-three-cols">
        {vault.length === 0 ? (
          <div className="empty-state">No passwords saved yet.</div>
        ) : (
          vault.map((entry, i) => {
            const Icon = iconForLabel(entry.label);
            return (
              <div key={i} className="card pv-card">
                <div className="pv-icon" aria-hidden="true"><Icon /></div>
                <div className="pv-title">{entry.label}</div>
                <div className="pv-actions">
                  <button
                    className="btn btn-main"
                    onClick={() => openEdit(i)}
                    {...(readOnly ? { "data-allow-readonly": "1" } : {})}
                  >
                    {readOnly ? "View" : "Edit"}
                  </button>
                  {!readOnly && (
                    <button className="btn btn-danger" onClick={() => handleDelete(i)}>Delete</button>
                  )}
                    
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ADD modal */}
      {showAdd && !readOnly && (
        <div className="modal-bg modal-bg--below-header pv-modal" onClick={() => setShowAdd(false)}>
          <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={handleAddGuard}>
            <h3>Add New Password</h3>

            <div className="pv-select-row">
              <div className="pv-net-icon" aria-hidden="true">
                {(() => {
                  const N = NETWORKS.find((n) => n.value === network);
                  return N?.Icon ? <N.Icon /> : <span style={{ width: 20, display: "inline-block" }} />;
                })()}
              </div>
              <div style={{ flex: 1 }}>
                <label className="label" htmlFor="net">Select Network</label>
                <select
                  id="net"
                  className="input"
                  value={network}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNetwork(val);
                    if (val !== "other") {
                      const N = NETWORKS.find((n) => n.value === val);
                      setNewLabel(N?.label || "");
                      setOtherNetwork("");
                    } else {
                      setNewLabel("");
                    }
                  }}
                  required
                >
                  <option value="" disabled>Choose a network…</option>
                  {NETWORKS.map((n) => (
                    <option key={n.value} value={n.value}>{n.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {network === "other" && (
              <div className="pv-field">
                <label className="label" htmlFor="otherLab">Other Network</label>
                <input
                  id="otherLab"
                  type="text"
                  className="input"
                  placeholder="Enter network name"
                  value={otherNetwork}
                  onChange={(e) => {
                    setOtherNetwork(e.target.value);
                    setNewLabel(e.target.value);
                  }}
                  required
                />
              </div>
            )}

            <div className="pv-select-row pv-select-row--ghost">
              <div className="pv-net-icon pv-net-icon--ghost" aria-hidden="true" />
              <div style={{ flex: 1 }}>
                <label className="label" htmlFor="pw1">Password</label>
                <input
                  id="pw1"
                  type="password"
                  className="input"
                  placeholder="Enter password"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="pv-select-row pv-select-row--ghost">
              <div className="pv-net-icon pv-net-icon--ghost" aria-hidden="true" />
              <div style={{ flex: 1 }}>
                <label className="label" htmlFor="pw2">Confirm Password</label>
                <input
                  id="pw2"
                  type="password"
                  className="input"
                  placeholder="Retype password"
                  value={newValue2}
                  onChange={(e) => setNewValue2(e.target.value)}
                  required
                />
              </div>
            </div>

            {error && <div className="empty-state" role="alert">{error}</div>}

            <div className="form-actions">
              <button className="btn btn-main" type="submit">Save</button>
              <button className="btn btn-cancel" type="button" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* EDIT modal */}
      {editOpen && (
        <div className="modal-bg modal-bg--below-header pv-modal" onClick={closeEdit}>
          <form
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              if (!editReadOnly) handleEditSave();
            }}
          >
             <h3>{editReadOnly ? "View Password" : "Edit Password"}</h3>

            <div className="pv-select-row">
              <div className="pv-net-icon" aria-hidden="true">
                {(() => {
                  const N = NETWORKS.find((n) => n.value === editNetwork);
                  return N?.Icon ? <N.Icon /> : <span style={{ width: 20, display: "inline-block" }} />;
                })()}
              </div>
              <div style={{ flex: 1 }}>
                <label className="label" htmlFor="enet">Select Network</label>
                <select
                  id="enet"
                  className="input"
                  value={editNetwork}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditNetwork(val);
                    if (val !== "other") {
                      const N = NETWORKS.find((n) => n.value === val);
                      setEditLabel(N?.label || "");
                      setEditOtherNetwork("");
                    } else {
                      setEditLabel("");
                    }
                  }}
                  required
                    disabled={editReadOnly}
                >
                  <option value="" disabled>Choose a network…</option>
                  {NETWORKS.map((n) => (
                    <option key={n.value} value={n.value}>{n.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {editNetwork === "other" && (
              <div className="pv-field">
                <label className="label" htmlFor="eotherLab">Other Network</label>
                <input
                  id="eotherLab"
                  type="text"
                  className="input"
                  placeholder="Enter network name"
                  value={editOtherNetwork}
                  onChange={(e) => {
                    setEditOtherNetwork(e.target.value);
                    setEditLabel(e.target.value);
                  }}
                  required
                    readOnly={editReadOnly}
                />
              </div>
            )}

            <div className="pv-field">
              <label className="label" htmlFor="epw1">Password</label>
              <input
                id="epw1"
                type={editShow ? "text" : "password"}
                className="input"
                placeholder="Enter password"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                required
                  readOnly={editReadOnly}
              />
            </div>

            <div className="pv-field">
              <label className="label" htmlFor="epw2">Confirm Password</label>
              <input
                id="epw2"
                type={editShow ? "text" : "password"}
                className="input"
                placeholder="Retype password"
                value={editValue2}
                onChange={(e) => setEditValue2(e.target.value)}
                required
                  readOnly={editReadOnly}
              />
            </div>

            {error && <div className="empty-state" role="alert">{error}</div>}

            <div className="form-actions">
              <button
                className="btn btn-main"
                type="button"
                onClick={() => setEditShow((s) => !s)}
                {...(editReadOnly ? { "data-allow-readonly": "1" } : {})}
              >
                {editShow ? "Hide" : "Show"}
              </button>
              <button
                className="btn btn-main"
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(editValue || "");
                    setCopiedIdx(editIdx);
                    setTimeout(() => setCopiedIdx(null), 1100);
                  } catch {}
                }}
                  {...(editReadOnly ? { "data-allow-readonly": "1" } : {})}
              >
                {copiedIdx === editIdx ? "Copied!" : "Copy"}
              </button>
              {!editReadOnly && <button className="btn btn-main" type="submit">Save</button>}
              <button className="btn btn-cancel" type="button" onClick={closeEdit}>
                {editReadOnly ? "Close" : "Cancel"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ✅ Unified video lightbox (replaces old modal) */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://www.youtube.com/watch?v=z5V0Z-hrtSI"
        title="How the Password Vault works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* DP drawer (shelf) */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Password Vault works</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button">
                  Print
                </button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button">
                  Close
                </button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>
                Store passwords securely with a master password only you know. Your entries are encrypted in your
                vault and can be edited or removed any time.
              </p>
              <ol>
                <li>
                  Use the <b>+</b> button to add a new password, pick the service, and save.
                </li>
                <li>
                  Click <b>{readOnly ? "View" : "Edit"}</b> on a card to {readOnly ? "see and copy" : "view or update"} the saved password.
                </li>
                <li>
                  Use <b>Reset Vault</b> (on the unlock screen) to clear everything if needed.
                </li>
              </ol>
              <p>
                <i>Tip:</i> Use long passphrases and avoid reusing passwords across services.
              </p>
            </div>
          </aside>
        </div>
      )}

      {/* Scoped styles for hero/DP UI + PV modal spacing */}
      <style>{`
        .header-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .header-actions{display:flex;align-items:center;gap:10px}
        .desktop-only{display:none} .mobile-only{display:block}
        @media(min-width:900px){.desktop-only{display:block}.mobile-only{display:none}}

        .hero-wrap{margin:6px 0 14px}
        .hero-img{width:100%;height:130px;object-fit:cover;border-radius:14px}

        .dp-help-row{display:flex;gap:10px;flex-wrap:wrap}
        .dp-help{border-radius:8px;padding:10px 14px;display:inline-flex;align-items:center;gap:10px;font-weight:700;color:#fff;border:none}
        .dp-help.read{background:#650202;color:#fff}
        .dp-help.see{background:#003353;color:#fff}
        .dp-help-ico{font-size:1.05rem}

        .dp-video{position:relative;width:100%;padding-top:56.25%;border-radius:10px;overflow:hidden;background:#000;margin-bottom:12px}
        .dp-video iframe{position:absolute;inset:0;width:100%;height:100%}
        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:10000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;height:calc(100vh - 56px);width:min(520px,95vw);background:#fff;box-shadow:-8px 0 24px rgba(0,0,0,0.15);border-top-left-radius:12px;border-bottom-left-radius:12px;display:flex;flex-direction:column;overflow:hidden}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto}

        /* =========================
           PV modal spacing normalisation
           ========================= */
        .pv-modal .modal-card{max-width:560px;width:95vw}
        .pv-modal .modal-card h3{margin:0 0 10px}
        .pv-modal .label{font-size:13px;color:#6b7280;font-weight:600;margin:6px 0 4px}
        .pv-modal .input{min-height:42px}
        .pv-modal .form-actions{display:flex;gap:10px;margin-top:12px;flex-wrap:wrap}

        .pv-modal .pv-field{display:grid;gap:6px;margin:8px 0}
        .pv-modal .pv-select-row{display:flex;align-items:center;gap:10px;margin:8px 0}
        .pv-modal .pv-select-row--ghost{margin-top:4px}
        .pv-modal .pv-net-icon{width:36px;height:36px;display:grid;place-items:center;border:1px solid #e5e7eb;border-radius:10px;background:#fff}
        .pv-modal .pv-net-icon--ghost{border-color:transparent;background:transparent}

        /* Cards grid untouched */
        .grid-three-cols{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:14px}
        .pv-card{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:10px;padding:12px}
        .pv-icon{width:40px;height:40px;border-radius:10px;background:#f3f4f6;display:grid;place-items:center;font-size:20px}
        .pv-title{font-weight:700}
        .pv-actions{display:flex;gap:8px;justify-self:end}

        /* Standalone setup/unlock forms minor tightening */
        .pv-standalone{max-width:560px;margin:0 auto}
        .pv-form-inner{display:grid;gap:8px}
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="passwordVault" />
    </div>
  );
}
